import React from 'react';
import _ from 'lodash';
import { Button, Select, Option, TextInput, Checkbox } from 'design-system';
import { makeStyles } from 'design-system/styles';
import { SubtaskStatus, ReviewLevel } from '@scale/shared/defaults';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { CircularProgress } from '@material-ui/core';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  SubtaskFilterOptionsResponseJSON,
  SubtaskFilterRequestJSON,
  FilterRequestOrdering,
} from '@scale/shared/types/project';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import Table from 'corp/components/UI/Table';

interface AdvancedFilterProps {
  projectId: string;
  justify?: string;
}

const AdvancedFilter: React.FC<AdvancedFilterProps> = ({ projectId, justify = 'start' }) => {
  const classes = useStyles();
  const { isRemoAdmin } = useRemoAdminCtx();

  const [isExpanded, setExpanded] = React.useState<boolean>(false);
  const [filterOptions, setFilterOptions] = React.useState<SubtaskFilterOptionsResponseJSON | null>(
    null,
  );
  const [statuses, setStatuses] = React.useState<SubtaskStatus[]>([]);
  const [reviewLevels, setReviewLevels] = React.useState<ReviewLevel[]>([]);
  const [projectNames, setProjectNames] = React.useState<string[]>([]);
  const [limit, setLimit] = React.useState<number | undefined>(undefined);
  const [claimed, setClaimed] = React.useState<boolean>(false);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [isCopied, setCopied] = React.useState<boolean>(false);
  const [subtasks, setSubtasks] = React.useState<any[] | null>(null);
  const [ordering, setOrdering] = React.useState<FilterRequestOrdering>(
    FilterRequestOrdering.MostRecent,
  );

  const expand = () => {
    lookupFilterOptions();
    setExpanded(true);
  };

  const lookupFilterOptions = () => {
    setLoading(true);
    apiFetch(null, `corp-api/lookup/${projectId}/advanced-lookup-options`, {
      method: 'GET',
    })
      .then((response: SubtaskFilterOptionsResponseJSON) => {
        setLoading(false);
        setFilterOptions(response);
        setProjectNames(Object.keys(response.subprojectMap));
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  const lookupSubtasks = () => {
    if (!filterOptions) return;
    setLoading(true);
    const subprojectMap = Object.fromEntries(
      Object.entries(filterOptions.subprojectMap).filter(([key]) => projectNames.includes(key)),
    );
    const body: SubtaskFilterRequestJSON = {
      reviewLevels,
      statuses,
      subprojectMap,
      claimed,
      limit,
      ordering,
    };
    apiFetch(null, `corp-api/lookup/${projectId}/advanced-lookup`, {
      method: 'POST',
      body,
    })
      .then((response: any) => {
        setLoading(false);
        setSubtasks(response.subtasks);
        setCopied(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  const copySubtasksToClipboard = () => {
    if (!subtasks) {
      return;
    }
    const contents = subtasks.map(s => s._id).join('\n');
    const el = document.createElement('textarea');
    el.value = contents;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopied(true);
  };

  const getSelectMultiHandler = function <T>(update: (newVal: T[]) => void) {
    return (e: React.ChangeEvent<{ value: T[] }>) => {
      update(e.target.value);
    };
  };

  return (
    <div
      className={classes.container}
      style={{ alignItems: justify, width: isRemoAdmin ? 'inherit' : 680 }}
    >
      {!isExpanded && (
        <Button startIcon={<FilterIcon />} type="tertiary" onClick={expand}>
          Filter for More Subtasks...
        </Button>
      )}
      {isExpanded && isLoading && (
        <div className={classes.spinnerContainer}>
          <CircularProgress size={24} />
        </div>
      )}
      {isExpanded && !isLoading && filterOptions && (
        <>
          <div className={classes.filtersContainer}>
            <Select
              value={ordering}
              onChange={(e: React.ChangeEvent<HTMLOptionElement>) =>
                setOrdering(e.target.value as FilterRequestOrdering)
              }
              label="Ordering"
            >
              {_.entries(FilterRequestOrdering).map(([key, orderingValue]) => (
                <Option key={orderingValue} value={orderingValue}>
                  {key}
                </Option>
              ))}
            </Select>
            <Select
              multiple
              value={statuses}
              onChange={getSelectMultiHandler(setStatuses)}
              label="Status(es)"
            >
              <Option disabled value="">
                Status(es)
              </Option>
              {filterOptions.statuses.map(status => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
            <Select
              multiple
              value={reviewLevels}
              onChange={getSelectMultiHandler(setReviewLevels)}
              label="Review Level(s)"
            >
              <Option disabled value="">
                Review Level(s)
              </Option>
              {filterOptions.reviewLevels.map(level => (
                <Option key={level} value={level}>
                  {level}
                </Option>
              ))}
            </Select>
            <Select
              multiple
              value={projectNames}
              onChange={getSelectMultiHandler(setProjectNames)}
              label="Project(s)"
            >
              <Option disabled value="">
                Project(s)
              </Option>
              {Object.keys(filterOptions.subprojectMap).map(name => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Select>
            <Checkbox
              label="Only Claimed"
              checked={claimed}
              onChange={() => setClaimed(!claimed)}
            />
          </div>
          <div>
            <Button
              onClick={lookupSubtasks}
              disabled={projectNames.length === 0}
              className={classes.lookupButton}
            >
              Search
            </Button>
            <TextInput
              type="number"
              placeholder="Limit (default 500)"
              value={limit}
              onChange={e => setLimit(+e.target.value)}
            />
          </div>
        </>
      )}
      {!isLoading && subtasks && (
        <>
          <div className={classes.responseContainer}>
            <div>Found {subtasks.length} Subtasks.</div>
            <Button onClick={copySubtasksToClipboard} disabled={isCopied}>
              {isCopied ? '✅ Copied' : 'Copy Ids'}
            </Button>
            <Button
              href={`/corp/v2/speed_audit?project=${projectId}&ids=${subtasks
                .map(s => s._id)
                .join(',')}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Audit
            </Button>
          </div>
          <Table
            data={subtasks}
            columns={[
              { accessor: '_id', Header: 'Subtask', width: 200 },
              { accessor: 'task', Header: 'Task', width: 200 },
              { accessor: 'project', Header: 'Project', width: 200 },
              { accessor: 'status', Header: 'Status' },
              { accessor: 'created_at', Header: 'Created At', width: 200 },
              { accessor: 'assignedSession', Header: 'Assigned Session', width: 300 },
            ]}
          />
        </>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16,
    marginBottom: 16,
    width: 680,
    '& > *': {
      marginTop: 8,
    },
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      marginBottom: 16,
    },
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
  },
  spinner: {
    width: 24,
    height: 24,
  },
  lookupButton: {
    width: 240,
    marginRight: 16,
  },
  responseContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    '& > *': {
      marginRight: 16,
    },
    background: '#00a2ff17',
    borderRadius: 8,
    padding: 16,
  },
});

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.6995 2.3999H3.30144C2.50201 2.3999 2.09862 3.36988 2.66506 3.93631L9.60038 10.8727V18.5999C9.60038 18.8936 9.74367 19.1688 9.98427 19.3372L12.9843 21.4365C13.5761 21.8508 14.4004 21.4309 14.4004 20.6992V10.8727L21.3359 3.93631C21.9012 3.371 21.5005 2.3999 20.6995 2.3999Z"
      fill="black"
    />
  </svg>
);

export default AdvancedFilter;
